import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoveMoneyContext } from '../../context/MoveMoneyContext';
import '../MoveMoney/MoveMoney.css';
import { fetchAvailablePaymentMethods, validatePaymentMethodSelection } from '../../api';

interface PaymentMethod {
  transferMethodCode: string;
  transferMethodCodeDescription: string;
  speed: string;
  transferFees: number;
  processingFee: number;
  conversionRate: number;
  recommended: boolean;
  paymentMethodLimit: number;
  paymentEffectiveInDays: number;
}

const PaymentMethods = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);
  const navigate = useNavigate();
  const moveMoneyContext = useContext(MoveMoneyContext);

  if (!moveMoneyContext) {
    throw new Error('MoveMoneyContext must be used within a MoveMoneyProvider');
  }

  const {
    transactionId,
    paymentMethod,
    setPaymentMethod,
    amount,
    fromAccount,
    beneficiaries,
    beneficiary,
    eventMapInstanceID,
    sourceInstanceID,
    setPaymentMethodResponse,
    setEventMapInstanceID,
    setSourceInstanceID
  } = moveMoneyContext;

  const selectedBeneficiary = beneficiaries.find(b => b.receiverId === beneficiary);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    console.log('Loading payment methods for transaction:', {
      transactionId,
      amount,
      fromAccount,
      eventMapInstanceID,
      sourceInstanceID
    });

    const loadPaymentMethods = async () => {
      if (!eventMapInstanceID || !sourceInstanceID) {
        setError('Missing required transaction context.');
        setLoadingPaymentMethods(false);
        return;
      }

      try {
        setLoadingPaymentMethods(true);
        setError(null);

        const transferDetails = {
          amount,
          accountNumber: fromAccount,
          currency: 'USD',
          eventMapInstanceId: eventMapInstanceID,
          sourceInstanceId: sourceInstanceID,
        };

        const response = await fetchAvailablePaymentMethods(transferDetails);

        if (response?.paymentMethodList) {
          setPaymentMethods(response.paymentMethodList);
          setEventMapInstanceID(response.eventMapInstanceID);
          setSourceInstanceID(response.sourceInstanceID);
        } else {
          setError('No payment methods available.');
        }
      } catch (err) {
        console.error('Failed to load payment methods:', err);
        setError('Failed to load payment methods. Please try again later.');
      } finally {
        setLoadingPaymentMethods(false);
      }
    };

    if (amount && fromAccount) {
      loadPaymentMethods();
    }
  }, [amount, fromAccount, eventMapInstanceID, sourceInstanceID]);


  const handleNext = async () => {
    if (!paymentMethod) return alert('Please select a payment method.');
    if (!selectedBeneficiary) return alert('Missing beneficiary details.');

    setIsLoading(true);
    setError(null);

    try {
      const selectedMethod = paymentMethods.find(pm => pm.transferMethodCode === paymentMethod);

      const payload = {
        customerID: localStorage.getItem('currentUserId') || 'nova.brooks',
        eventMapInstanceId: eventMapInstanceID,
        sourceInstanceId: sourceInstanceID,
        externalTransferDetailsList: [
          {
            accountNumber: selectedBeneficiary.receiverAccountNumber,
            beneficiaryName: `${selectedBeneficiary.receiverFirstName} ${selectedBeneficiary.receiverLastName}`,
            beneficiaryEmail: selectedBeneficiary.receiverEmail,
            countryCode: selectedBeneficiary.receiverCountry || 'US',
            beneficiaryPhone: '1215610654', // can be added to context if needed
            beneficiaryStreetName: 'Street577',
            beneficiaryRestricted: false,
            beneficiaryAddressLine1: '456',
            beneficiaryAddressLine2: '890',
            beneficiaryZIPCode: null,
            beneficiaryCity: 'New York',
            beneficiaryState: 'New York',
            paymentCurrency: selectedBeneficiary.receiverCurrency,
            benificiaryOptionsSelected: [
              {
                externalReferenceId: transactionId,
                transferMethodName: paymentMethod,
                transferAmount: parseFloat(amount),
                speed: selectedMethod?.speed || 'Instant'
              }
            ]
          }
        ],
        fraudDetectionRequest: {
          beneficiaryName: `${selectedBeneficiary.receiverFirstName} ${selectedBeneficiary.receiverLastName}`,
          transferAmount: amount
        },
        transferDetails: {
          accountNumber: fromAccount,
          amount: parseFloat(amount),
          core: "finxact",
          currencyCode: "USD",
          transferDate: new Date().toISOString().split('T')[0]
        }
      };

      const validationResponse = await validatePaymentMethodSelection(payload);
      console.log('Validation response:', validationResponse);

      if (!validationResponse?.isFraudDetected) {
        setPaymentMethodResponse(validationResponse);
        navigate('/move-money/confirmation');
      } else {
        setError('Validation failed. Please try again.');
      }

    } catch (err) {
      console.error('Step 4 validation failed:', err);
      setError('Failed to validate payment method. Try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/move-money/select-account');
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  const getPaymentMethodDescription = (method: PaymentMethod) =>
    method.transferMethodCodeDescription || method.transferMethodCode;

  const getPaymentMethodFee = (method: PaymentMethod) => {
    const fees = [];
    if (method.transferFees > 0) {
      fees.push(`Transfer fee: ${formatCurrency(method.transferFees)}`);
    }
    if (method.processingFee > 0) {
      fees.push(`Processing fee: ${formatCurrency(method.processingFee)}`);
    }
    return fees.length ? fees.join(' • ') : 'No fees';
  };

  const getPaymentMethodLimit = (method: PaymentMethod) =>
    `Limit: ${formatCurrency(method.paymentMethodLimit)}`;

  if (loadingPaymentMethods) {
    return (
      <div className="flow-step">
        <h2 className="step-header">Select Payment Method</h2>
        <div className="loading">Loading payment methods...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flow-step">
        <h2 className="step-header">Select Payment Method</h2>
        <div className="error">{error}</div>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="flow-step">
      <h2 className="step-header">Select Payment Method</h2>
      {paymentMethods.length === 0 ? (
        <div className="no-payment-methods">No payment methods available.</div>
      ) : (
        <ul className="selection-list">
          {paymentMethods.map((method) => (
            <li
              key={method.transferMethodCode}
              className={`selection-item ${method.recommended ? 'recommended' : ''}`}
            >
              <label>
                <input
                  type="radio"
                  name="paymentMethod"
                  value={method.transferMethodCode}
                  checked={paymentMethod === method.transferMethodCode}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                <div className="payment-method-info">
                  <div className="payment-method-name">
                    {getPaymentMethodDescription(method)}
                    {method.recommended && <span className="recommended-badge">Recommended</span>}
                  </div>
                  <div className="payment-method-speed">{method.speed}</div>
                  <div className="payment-method-fee">{getPaymentMethodFee(method)}</div>
                  <div className="payment-method-limit">{getPaymentMethodLimit(method)}</div>
                </div>
              </label>
            </li>
          ))}
        </ul>
      )}
      <div className="navigation-buttons">
        <button onClick={handleBack} disabled={isLoading}>Back</button>
        <button onClick={handleNext} disabled={isLoading || paymentMethods.length === 0}>
          {isLoading ? 'Submitting...' : 'Next'}
        </button>
      </div>
    </div>
  );
};

export default PaymentMethods;
